import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  IconButton, LinearProgress, ToggleButton, ToggleButtonGroup, Tooltip, Typography
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  FileDownload as DownloadIcon
} from '@mui/icons-material';
import { apiBlobError, downloadBlob, plural } from '../../Documents/format';
import { notify } from '../../../notifications';
import { BUCKET_LABELS, DEFAULT_EXPORT_COLUMNS, EXPORT_COLUMNS, EXPORT_FORMATS, OPTION_FIELDS, TABLE_COLUMNS } from '../constants';
import { fmtDay, monthLabel } from '../format';
import { isCancelled, journalApi } from '../journalApi';
import { ExportCount, ExportFormat, JournalFilters, JournalMeta, SortState } from '../types';
import { summary } from './FilterControls';

interface Props {
  open: boolean;
  meta: JournalMeta;
  /** Применённые фильтры журнала - выгрузка идёт ровно по ним */
  filters: JournalFilters;
  /** Текущая сортировка таблицы - в том же порядке строки попадут в файл */
  sort: SortState;
  onClose: () => void;
}

const BORDER = '#e2e8f0';
const BARCODE_IMAGE = 'barcodeImage';
const ALL_COLUMN_KEYS = EXPORT_COLUMNS.map(c => c.key);

const sectionLabel = { mb: 0.75, fontSize: '0.8rem', fontWeight: 600, color: 'text.secondary' } as const;
const filterChipSx = { height: 24, fontSize: '0.74rem', borderRadius: 1.5, bgcolor: 'rgba(37,99,235,0.07)', color: '#1e40af', fontWeight: 600 } as const;

/** Подписи применённых фильтров - в окне выгрузки они только для сведения */
function filterLabels(f: JournalFilters, pharmacyNames: Record<string, string>): string[] {
  const out = [`Период: ${monthLabel(f.month || null)}`];
  if (f.pharmacies.length) out.push(`Аптеки: ${summary(f.pharmacies.map(g => pharmacyNames[g] || g))}`);
  if (f.search.trim()) out.push(`Поиск: «${f.search.trim()}»`);
  if (f.bucket) out.push(`Срок: ${BUCKET_LABELS[f.bucket]}`);
  if (f.expiryFrom || f.expiryTo) {
    out.push(`Годен: ${f.expiryFrom ? `с ${fmtDay(f.expiryFrom)} ` : ''}${f.expiryTo ? `по ${fmtDay(f.expiryTo)}` : ''}`.trim());
  }
  OPTION_FIELDS.forEach(o => {
    const pick = f.picks[o.field];
    if (pick) out.push(`${o.label}: ${pick.label}`);
  });
  return out;
}

const JournalExportDialog: React.FC<Props> = ({ open, meta, filters, sort, onClose }) => {
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [columns, setColumns] = useState<string[]>(DEFAULT_EXPORT_COLUMNS);
  const [showColumns, setShowColumns] = useState(false);
  const [count, setCount] = useState<ExportCount | null>(null);
  const [counting, setCounting] = useState(false);
  const [countError, setCountError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const downloadCtrl = useRef<AbortController | null>(null);

  const pharmacyNames = useMemo(() => {
    const map: Record<string, string> = {};
    meta.pharmacies.forEach(p => { map[p.guid] = p.name; });
    return map;
  }, [meta.pharmacies]);
  const labels = useMemo(() => filterLabels(filters, pharmacyNames), [filters, pharmacyNames]);
  const sortLabel = useMemo(() => {
    const col = TABLE_COLUMNS.find(c => c.sort === sort.sortBy);
    return `${col ? col.label : sort.sortBy}, ${sort.sortOrder === 'asc' ? 'по возрастанию' : 'по убыванию'}`;
  }, [sort]);

  useEffect(() => {
    if (!open) return;
    setShowColumns(false);
    setCount(null);
    setCountError(null);
  }, [open]);

  // Незавершённая выгрузка отменяется при размонтировании
  useEffect(() => () => downloadCtrl.current?.abort(), []);

  useEffect(() => {
    if (!open) return undefined;
    const ctrl = new AbortController();
    setCounting(true);
    journalApi.exportCount(filters, ctrl.signal)
      .then(res => { setCount(res); setCountError(null); setCounting(false); })
      .catch(err => {
        if (isCancelled(err)) return;
        setCountError('Не удалось подсчитать записи');
        setCounting(false);
      });
    return () => ctrl.abort();
  }, [open, filters]);

  // Картинки штрихкодов есть только в Excel: для CSV колонка не отправляется
  const sentColumns = useMemo(
    () => (format === 'xlsx' ? columns : columns.filter(k => k !== BARCODE_IMAGE)),
    [columns, format]
  );
  const images = sentColumns.indexOf(BARCODE_IMAGE) !== -1;

  const toggleColumn = useCallback((key: string) => {
    // порядок колонок в файле всегда как в справочнике, а не по порядку кликов
    setColumns(prev => ALL_COLUMN_KEYS.filter(k => (k === key ? prev.indexOf(k) === -1 : prev.indexOf(k) !== -1)));
  }, []);

  const reset = useCallback(() => {
    setColumns(DEFAULT_EXPORT_COLUMNS);
    setFormat('xlsx');
  }, []);

  const close = useCallback(() => {
    downloadCtrl.current?.abort();
    onClose();
  }, [onClose]);

  const download = useCallback(async () => {
    const ctrl = new AbortController();
    downloadCtrl.current = ctrl;
    setDownloading(true);
    try {
      const file = await journalApi.exportFile(filters, sort, format, sentColumns, ctrl.signal);
      downloadBlob(file.filename, file.blob);
      onClose();
    } catch (err) {
      if (!isCancelled(err)) notify.error(await apiBlobError(err, 'Не удалось сформировать выгрузку'));
    } finally {
      downloadCtrl.current = null;
      setDownloading(false);
    }
  }, [filters, sort, format, sentColumns, onClose]);

  const tooLarge = Boolean(count?.tooLarge);
  const imagesTooMany = Boolean(images && count && count.count > count.barcodeRows);
  // со штрихкодами-картинками всегда один файл
  const files = images ? 1 : count?.files ?? 0;
  const empty = count !== null && count.count === 0;
  const canDownload = !downloading && !counting && count !== null && !tooLarge && !imagesTooMany && !empty && sentColumns.length > 0;
  const shownCount = sentColumns.length;

  return (
    <Dialog
      open={open} onClose={downloading ? undefined : close} maxWidth={false}
      PaperProps={{ sx: { width: '100%', maxWidth: 660, borderRadius: 3, boxShadow: '0 20px 48px rgba(15, 23, 42, 0.16)', overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ py: 1.5, px: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${BORDER}` }}>
        <Typography variant="subtitle1" fontWeight={700}>Выгрузка журнала сроков</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ToggleButtonGroup
            size="small" exclusive value={format} disabled={downloading}
            onChange={(_, v: ExportFormat | null) => { if (v) setFormat(v); }}
            sx={{ height: 30, '& .MuiToggleButton-root': { px: 1.25, py: 0, fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', whiteSpace: 'nowrap' } }}
          >
            {EXPORT_FORMATS.map(f => <ToggleButton key={f.value} value={f.value}>{f.label}</ToggleButton>)}
          </ToggleButtonGroup>
          <IconButton onClick={close} size="small" aria-label="Закрыть" sx={{ color: 'text.secondary', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ mt: 2.5 }}>
          <Typography variant="body2" sx={sectionLabel}>Выгружаются записи по текущим фильтрам журнала</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {labels.map(l => <Chip key={l} size="small" label={l} sx={{ ...filterChipSx, maxWidth: '100%' }} />)}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Порядок строк: {sortLabel}. Чтобы изменить набор записей, закройте окно и поменяйте фильтры или сортировку таблицы.
          </Typography>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500 }}>Колонки в файле:</Typography>
              <Typography variant="caption" color="text.secondary">
                Выбрано {shownCount} из {format === 'xlsx' ? ALL_COLUMN_KEYS.length : ALL_COLUMN_KEYS.length - 1}
              </Typography>
            </Box>
            <Button
              size="small" variant="text" onClick={() => setShowColumns(v => !v)}
              sx={{ textTransform: 'none', fontSize: '0.75rem', height: 26, borderRadius: 1.25, color: showColumns ? 'primary.main' : 'text.secondary' }}
            >
              {showColumns ? 'Свернуть' : 'Настроить…'}
            </Button>
          </Box>

          <Collapse in={showColumns} timeout={180} unmountOnExit>
            <Box sx={{ mt: 1, p: 1.25, borderRadius: 2, border: `1px solid ${BORDER}`, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {EXPORT_COLUMNS.map(col => {
                const unavailable = Boolean(col.xlsxOnly) && format !== 'xlsx';
                const checked = !unavailable && columns.indexOf(col.key) !== -1;
                const chip = (
                  <Chip
                    key={col.key} label={col.label} size="small" clickable={!unavailable} disabled={unavailable || downloading}
                    onClick={() => toggleColumn(col.key)}
                    icon={checked ? <CheckIcon sx={{ fontSize: '14px !important' }} /> : undefined}
                    color={checked ? 'primary' : 'default'} variant={checked ? 'filled' : 'outlined'}
                    sx={{ borderRadius: 1.5, height: 28, fontSize: '0.75rem', fontWeight: checked ? 600 : 400 }}
                  />
                );
                return unavailable
                  ? <Tooltip key={col.key} title="Картинки штрихкодов есть только в Excel"><span>{chip}</span></Tooltip>
                  : chip;
              })}
            </Box>
          </Collapse>
        </Box>

        {images && count && !imagesTooMany && (
          <Alert severity="info">
            Колонка с картинками штрихкодов: файл формируется дольше и всегда одним файлом (до {count.barcodeRows.toLocaleString('ru-RU')} строк).
          </Alert>
        )}
        {imagesTooMany && count && (
          <Alert severity="warning">
            Картинки штрихкодов выгружаются не более чем для {count.barcodeRows.toLocaleString('ru-RU')} строк, а записей {count.count.toLocaleString('ru-RU')}.
            Сузьте фильтры или уберите колонку «Штрихкод (картинка)».
          </Alert>
        )}
        {tooLarge && count && (
          <Alert severity="warning">
            Записей больше допустимого предела выгрузки ({count.maxRows.toLocaleString('ru-RU')}). Выберите период или добавьте фильтры.
          </Alert>
        )}
        {!tooLarge && !images && count && count.files > 1 && (
          <Alert severity="info">
            Записей больше {count.partRows.toLocaleString('ru-RU')}: выгрузка будет разделена на {count.files} {plural(count.files, 'файл', 'файла', 'файлов')} и упакована в ZIP-архив.
          </Alert>
        )}
        {empty && <Alert severity="info">По текущим фильтрам записей нет - выгружать нечего.</Alert>}
        {countError && <Alert severity="error">{countError}</Alert>}
        {downloading && (
          <Box>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Формируем файл. Большие выгрузки занимают несколько минут - окно закрывать не нужно.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ py: 1.5, px: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.25, borderRadius: 1.5, bgcolor: 'rgba(25, 118, 210, 0.06)', height: 32 }}>
          <Typography variant="caption" color="text.secondary">Записей:</Typography>
          {counting
            ? <CircularProgress size={12} thickness={5} />
            : <Typography variant="caption" fontWeight={700} color="primary.main">{count ? count.count.toLocaleString('ru-RU') : '-'}</Typography>}
          {count && files > 1 && !counting && (
            <Tooltip title="Количество файлов в архиве">
              <Typography variant="caption" color="text.secondary">Файлов: {files}</Typography>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Button size="small" color="inherit" onClick={reset} disabled={downloading} sx={{ textTransform: 'none', fontSize: '0.8rem', color: 'text.secondary', minWidth: 0, px: 1 }}>
            Сбросить
          </Button>
          <Button size="small" color="inherit" onClick={close} sx={{ textTransform: 'none', fontSize: '0.8rem', minWidth: 0, px: 1.25 }}>
            Отмена
          </Button>
          <Button
            variant="contained" size="small" onClick={download} disabled={!canDownload}
            startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon fontSize="small" />}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', height: 34, px: 2, borderRadius: 1.5, whiteSpace: 'nowrap', boxShadow: 'none' }}
          >
            {downloading ? 'Формирование…' : files > 1 ? 'Скачать ZIP' : `Скачать ${format.toUpperCase()}`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default JournalExportDialog;
